import {
  AvailableAuthorizationsData,
  AvailableServicesData,
  CommonStateMachineContext,
  EditableStep,
  MinorUser,
  ResultType,
  StepType,
  User,
  WizardData,
} from '@digila-ui/types'
import { DigiLaParam, DigiLaUserInfoResponse, MinorApplicationStatus } from '@shared/digila'
import { assertValue } from '@shared/helpers'
import { ConfigResponse, DigiliFlow } from '@shared/types'
import { KycWizardData } from '@ui-common/kyc'
import { KycFinishedHandlerInput } from '@ui-common/kyc/KycWizard'
import { AuthenticatedEvent, CommonWizardContext, SubmitDataEvent as CommonSubmitEvent } from '@ui-common/state'
import { GetDataDoneEvent } from '@ui-common/steps/GetDataStep'
import { InitializedEvent, NextStepEvent as CommonNextStepEvent, PreviousStepEvent } from '@ui-common/types'
import { assertEvent, assign, setup } from 'xstate'

export const ExitEventType = 'EXIT'
export const SubmitEventType = 'SUBMIT'
export const EditEventType = 'EDIT'
export const GuardianKycDoneEventType = 'GUARDIAN_KYC_DONE'
export const GetMinorDataDoneEventType = 'GET_MINOR_DATA_DONE'
export const WizardDoneEventType = 'WIZARD_DONE'
export const ApplicationRejectedEventType = 'APPLICATION_REJECTED'
export const MinorKycDoneEventType = 'MINOR_KYC_DONE'
export const DigiliRequiredEventType = 'DIGILI_REQUIRED'
export const ResetEventType = 'RESET'

export const SERVICE_WIZARD_STATE = 'ServiceWizard'

type ExitEvent = { type: typeof ExitEventType }
export type SubmitDataEvent = CommonSubmitEvent<WizardData>
export type GuardianKycDoneEvent = { type: typeof GuardianKycDoneEventType; data: KycFinishedHandlerInput }

export type EditEvent = { type: typeof EditEventType; step: EditableStep }

export type NextStepEvent = CommonNextStepEvent<WizardData>

type GetMinorData = {
  applicationData: WizardData
  minor: MinorUser
}

export type GetMinorEvent = {
  type: typeof GetMinorDataDoneEventType
  data: GetMinorData
}
export type WizardDoneEvent = { type: typeof WizardDoneEventType; data: WizardData }
export type ApplicationRejectedEvent = { type: typeof ApplicationRejectedEventType }
export type MinorKycDoneEvent = { type: typeof MinorKycDoneEventType; data: KycFinishedHandlerInput }
export type DigiliRequiredEvent = { type: typeof DigiliRequiredEventType; data: DigiliFlow }
export type ResetEvent = { type: typeof ResetEventType }

export type WizardEvent =
  | ExitEvent
  | PreviousStepEvent
  | NextStepEvent
  | SubmitDataEvent
  | AuthenticatedEvent
  | GetDataDoneEvent<User>
  | GuardianKycDoneEvent
  | GetMinorEvent
  | EditEvent
  | InitializedEvent
  | WizardDoneEvent
  | ApplicationRejectedEvent
  | MinorKycDoneEvent
  | DigiliRequiredEvent
  | ResetEvent

export interface ConfigContext {
  config: ConfigResponse<DigiLaParam>
}

export interface WizardStateContext
  extends CommonStateMachineContext,
    CommonWizardContext<User, WizardData, DigiLaParam>,
    ConfigContext,
    AvailableServicesData,
    AvailableAuthorizationsData {
  minor: MinorUser | null
  result?: ResultType
}

export type WizardMachineType = ReturnType<typeof createWizardMachine>

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export const createWizardMachine = () => {
  return setup({
    types: {
      context: {} as WizardStateContext,
      events: {} as WizardEvent,
      input: {} as WizardStateContext,
    },
    actions: {
      updateWizardData: assign((_, data: WizardData) => {
        return {
          wizardData: data,
        }
      }),
      updateMinor: assign((context, kycWizardData: KycWizardData) => {
        const minor = assertValue(context.context.minor, 'context.context.minor')
        return {
          minor: {
            ...minor,
            kycInfo: {
              ...minor.kycInfo,
              ...kycWizardData.politicalInfo,
              ...kycWizardData.paymentsInfo,
              birthCountry: kycWizardData.basicInfo?.birthCountry,
              residenceCountry: kycWizardData.basicInfo?.residenceCountry,
              nationalities: kycWizardData.basicInfo?.nationalities,
              occupation: kycWizardData.basicInfo?.occupation,
              taxationCountries: kycWizardData.taxInfo?.taxationCountries,
              isTaxableInUsa: kycWizardData.taxInfo?.isTaxableInUsa,
            },
          },
        }
      }),
      saveAuthenticationData: assign((_, authToken: string) => {
        return { authToken }
      }),
      saveUserInfo: assign((_, user: DigiLaUserInfoResponse) => {
        return { user }
      }),
      saveMinorUserInfo: assign((_, data: GetMinorData) => {
        return {
          minor: data.minor,
          availableServices: data.minor.availableServices,
          availableAuthorizations: data.minor.availableAuthorizations,
          wizardData: data.applicationData,
        }
      }),
      saveConfig: assign((_, data?: ConfigResponse<DigiLaParam>) => {
        return { config: assertValue(data, 'saveConfig data') }
      }),
      completeGuardianKyc: assign(({ context }) => {
        return {
          user: {
            ...context.user,
            isKycRequired: false,
          },
        }
      }),
      saveResult: assign((_, data: ResultType) => {
        return { result: data }
      }),
      reset: assign(({ context }) => {
        const { authToken, config, user } = context

        return {
          authToken,
          config,
          user,
          minor: null,
          wizardData: {},
          availableServices: [],
          availableAuthorizations: [],
        } satisfies WizardStateContext
      }),
    },
    guards: {
      isEditing: ({ context }) => {
        return !!context.isEditing
      },
      isKycRequired: ({ context }) => {
        return !!context.user.isKycRequired
      },
      hasOpenInProgressApplication: ({ event }) => {
        assertEvent(event, 'GET_MINOR_DATA_DONE')
        return (
          !!event.data.minor.pendingApplication &&
          event.data.minor.pendingApplication?.status === MinorApplicationStatus.InProgress
        )
      },
      hasPendingApprovalApplication: ({ event }) => {
        assertEvent(event, 'GET_MINOR_DATA_DONE')
        return (
          !!event.data.minor.pendingApplication &&
          event.data.minor.pendingApplication?.status === MinorApplicationStatus.WaitingForApproval &&
          event.data.minor.isGuardianApplicationInitiator
        )
      },
    },
  }).createMachine({
    id: 'wizardMachine',
    initial: StepType.Initialize,
    context: ({ input }) => input,
    states: {
      [StepType.Initialize]: {
        id: StepType.Initialize,
        on: {
          INITIALIZED: [
            {
              target: StepType.Entrance,
              actions: { type: 'saveConfig', params: ({ event }) => event.data },
            },
          ],
        },
      },
      [StepType.Entrance]: {
        id: StepType.Entrance,
        on: {
          NEXT_STEP: { target: StepType.Authenticate },
        },
      },
      [StepType.Authenticate]: {
        id: StepType.Authenticate,
        on: {
          AUTHENTICATION_DONE: [
            {
              target: StepType.GetUserInfo,
              actions: { type: 'saveAuthenticationData', params: ({ event }) => event.data.authToken },
            },
          ],
        },
      },
      [StepType.GetUserInfo]: {
        id: StepType.GetUserInfo,
        on: {
          GET_DATA_DONE: [
            {
              target: StepType.MinorSsn,
              actions: { type: 'saveUserInfo', params: ({ event }) => event.data },
            },
          ],
        },
      },
      [StepType.MinorSsn]: {
        id: StepType.MinorSsn,
        entry: ['reset'],
        on: {
          GET_MINOR_DATA_DONE: [
            {
              guard: 'hasOpenInProgressApplication',
              target: StepType.End,
              actions: { type: 'saveResult', params: ResultType.ApplicationPending },
            },
            {
              guard: 'hasPendingApprovalApplication',
              target: StepType.End,
              actions: { type: 'saveResult', params: ResultType.ApplicationWaitingForApproval },
            },
            {
              target: StepType.KycGate,
              guard: 'isKycRequired',
              actions: {
                type: 'saveMinorUserInfo',
                params: ({ event }) => event.data,
              },
            },
            {
              target: SERVICE_WIZARD_STATE,
              actions: {
                type: 'saveMinorUserInfo',
                params: ({ event }) => event.data,
              },
            },
          ],
          DIGILI_REQUIRED: [
            {
              guard: ({ event }) => event.data === DigiliFlow.DigiLi,
              target: StepType.DigiliRequiredGate,
            },
            {
              target: StepType.DigipaRequiredGate,
            },
          ],
        },
      },
      [StepType.DigipaRequiredGate]: {
        id: StepType.DigipaRequiredGate,
        type: 'final',
      },
      [StepType.DigiliRequiredGate]: {
        id: StepType.DigiliRequiredGate,
        type: 'final',
      },
      [StepType.KycGate]: {
        id: StepType.KycGate,
        on: {
          NEXT_STEP: { target: StepType.GuardianKyc },
        },
      },
      [StepType.GuardianKyc]: {
        id: StepType.GuardianKyc,
        on: {
          GUARDIAN_KYC_DONE: [
            {
              target: SERVICE_WIZARD_STATE,
              actions: 'completeGuardianKyc',
              guard: ({ event }) => event.data.result.isKycValid,
            },
            {
              target: StepType.End,
              actions: { type: 'saveResult', params: ResultType.GuardianKycInvalid },
            },
          ],
        },
      },
      [SERVICE_WIZARD_STATE]: {
        id: SERVICE_WIZARD_STATE,
        on: {
          WIZARD_DONE: {
            target: StepType.End,
            actions: [
              { type: 'updateWizardData', params: ({ event }) => event.data },
              {
                type: 'saveResult',
                params: ({ context: { minor } }) =>
                  minor?.hasTwoGuardians && minor.isGuardianApplicationInitiator
                    ? ResultType.ApplicationWaitingForApproval
                    : ResultType.ApplicationSent,
              },
            ],
          },
          APPLICATION_REJECTED: {
            target: StepType.End,
            actions: [{ type: 'saveResult', params: ResultType.ApplicationRejected }],
          },
          MINOR_KYC_DONE: [
            {
              guard: ({ event }) => event.data.result.isKycValid,
              actions: { type: 'updateMinor', params: ({ event }) => event.data.kycWizardData },
            },
            { target: StepType.End, actions: { type: 'saveResult', params: ResultType.MinorKycInvalid } },
          ],
          RESET: {
            target: StepType.MinorSsn,
          },
        },
      },
      [StepType.End]: {
        id: StepType.End,
      },
    },
    on: {
      SUBMIT: {
        actions: { type: 'updateWizardData', params: ({ event }) => event.data },
      },
    },
  })
}
